import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './EkinextDetails.css';

const EkinextDetails = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [clients, setClients] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editingProduct, setEditingProduct] = useState({});
  const [newClient, setNewClient] = useState({ client_code: '', phone: '', email: '' });
  const [newFeedback, setNewFeedback] = useState({ client_id: '', feedback_text: '' });
  const [editingFeedback, setEditingFeedback] = useState({});

  const isAdmin = user?.role === 'admin';

  function formatDateIT(dateString) {
    if (!dateString) return '-';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  }

  useEffect(() => {
    loadProductDetails();
  }, [id]);

  async function loadProductDetails() {
    setLoading(true);
    try {
      const res = await api.getEkinextProductDetails(id);
      if (res.success) {
        setProduct(res.product);
        setClients(res.clients || []);
        setFeedbacks(res.feedbacks || []);
      }
    } catch (err) {
      console.error('Errore caricamento dettagli:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveProduct() {
    const updatedProduct = { ...product, ...editingProduct };
    try {
      const res = await api.updateEkinextProduct(updatedProduct);
      if (res.success) {
        await loadProductDetails();
        setEditingProduct({});
      } else {
        alert('Errore salvataggio: ' + res.error);
      }
    } catch (err) {
      alert('Errore di connessione');
    }
  }

  async function handleAddClient(e) {
    e.preventDefault();
    try {
      const res = await api.addEkinextClient({ ...newClient, product_id: id });
      if (res.success) {
        await loadProductDetails();
        setNewClient({ client_code: '', phone: '', email: '' });
      } else {
        alert('Errore aggiunta cliente: ' + res.error);
      }
    } catch (err) {
      alert('Errore di connessione');
    }
  }

  async function handleClientCheckbox(clientId, field, currentValue) {
    try {
      const res = await api.updateEkinextClientStatus({
        client_id: clientId,
        field: field,
        value: !currentValue
      });
      if (res.success) {
        await loadProductDetails();
      }
    } catch (err) {
      alert('Errore aggiornamento stato');
    }
  }

  async function handleAddFeedback(e) {
    e.preventDefault();
    try {
      const res = await api.addEkinextFeedback({ ...newFeedback, product_id: id });
      if (res.success) {
        await loadProductDetails();
        setNewFeedback({ client_id: '', feedback_text: '' });
      } else {
        alert('Errore creazione feedback: ' + res.error);
      }
    } catch (err) {
      alert('Errore di connessione');
    }
  }

  async function handleUpdateFeedback(feedbackId) {
    const updatedText = editingFeedback[feedbackId];
    try {
      const res = await api.updateEkinextFeedback({ id: feedbackId, feedback_text: updatedText });
      if (res.success) {
        await loadProductDetails();
        setEditingFeedback(prev => {
          const newState = { ...prev };
          delete newState[feedbackId];
          return newState;
        });
      }
    } catch (err) {
      alert('Errore aggiornamento feedback');
    }
  }

  async function handleDeleteFeedback(feedbackId) {
    if (!window.confirm('Eliminare questo feedback?')) return;
    try {
      const res = await api.deleteEkinextFeedback(feedbackId);
      if (res.success) {
        await loadProductDetails();
      }
    } catch (err) {
      alert('Errore eliminazione feedback');
    }
  }

  const quillModules = {
    toolbar: [
      ['bold', 'underline'],
      [{ 'list': 'bullet' }, { 'list': 'ordered' }]
    ]
  };

  if (loading) {
    return <div className="container mt-5 text-center">Caricamento...</div>;
  }

  if (!product) {
    return <div className="container mt-5 alert alert-danger">Prodotto non trovato</div>;
  }

  const currentProduct = { ...product, ...editingProduct };
  const hasProductChanges = Object.keys(editingProduct).length > 0;

  return (
    <div className="ekinext-details-container">
      {/* HEADER */}
      <div className="details-header">
        <h2>Dettagli Ekinext: {product.product_name}</h2>
        <button className="btn btn-secondary" onClick={() => navigate('/ekinext')}>
          ← Torna alla Lista
        </button>
      </div>

      {/* BOX INFO PRODOTTO - BLU */}
      <div className="card details-card info-card">
        <div className="card-header">
          <h5 className="mb-0">📋 Informazioni Prodotto</h5>
          {hasProductChanges && isAdmin && (
            <button className="btn btn-success btn-sm" onClick={handleSaveProduct}>
              Salva Modifiche
            </button>
          )}
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">Nome Prodotto</label>
              {isAdmin ? (
                <input
                  type="text"
                  className="form-control"
                  value={currentProduct.product_name}
                  onChange={e => setEditingProduct(prev => ({ ...prev, product_name: e.target.value }))}
                />
              ) : (
                <p>{product.product_name}</p>
              )}
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">Tipologia Prodotto</label>
              {isAdmin ? (
                <select
                  className="form-select"
                  value={currentProduct.product_type}
                  onChange={e => setEditingProduct(prev => ({ ...prev, product_type: e.target.value }))}
                >
                  <option value="software">Software</option>
                  <option value="hardware">Hardware</option>
                </select>
              ) : (
                <p>{product.product_type}</p>
              )}
            </div>
          </div>

          {currentProduct.product_type === 'hardware' && (
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Pezzi a Disposizione</label>
                {isAdmin ? (
                  <input
                    type="number"
                    className="form-control"
                    value={currentProduct.available_units || ''}
                    onChange={e => setEditingProduct(prev => ({ ...prev, available_units: e.target.value }))}
                  />
                ) : (
                  <p>{product.available_units || '-'}</p>
                )}
              </div>
            </div>
          )}

          <div className="row">
            <div className="col-md-3 mb-3">
              <label className="form-label fw-bold">Data Invio Email</label>
              {isAdmin ? (
                <input
                  type="date"
                  className="form-control"
                  value={currentProduct.email_sent_date || ''}
                  onChange={e => setEditingProduct(prev => ({ ...prev, email_sent_date: e.target.value }))}
                />
              ) : (
                <p>{formatDateIT(product.email_sent_date)}</p>
              )}
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label fw-bold">Data Avvio</label>
              {isAdmin ? (
                <input
                  type="date"
                  className="form-control"
                  value={currentProduct.start_date || ''}
                  onChange={e => setEditingProduct(prev => ({ ...prev, start_date: e.target.value }))}
                />
              ) : (
                <p>{formatDateIT(product.start_date)}</p>
              )}
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label fw-bold">Data Chiusura</label>
              {isAdmin ? (
                <input
                  type="date"
                  className="form-control"
                  value={currentProduct.close_date || ''}
                  onChange={e => setEditingProduct(prev => ({ ...prev, close_date: e.target.value }))}
                />
              ) : (
                <p>{formatDateIT(product.close_date)}</p>
              )}
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label fw-bold">Stato Ekinext</label>
              {isAdmin ? (
                <select
                  className="form-select"
                  value={currentProduct.status}
                  onChange={e => setEditingProduct(prev => ({ ...prev, status: e.target.value }))}
                >
                  <option value="pianificato">Pianificato</option>
                  <option value="in_corso">In Corso</option>
                  <option value="concluso">Concluso</option>
                  <option value="abortito">Abortito</option>
                </select>
              ) : (
                <p>{product.status}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* BOX AGGIUNGI CLIENTE - VERDE */}
      {isAdmin && (
        <div className="card details-card add-client-card">
          <div className="card-header">
            <h5 className="mb-0">➕ Aggiungi Cliente</h5>
          </div>
          <div className="card-body">
            <form onSubmit={handleAddClient}>
              <div className="row">
                <div className="col-md-4">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Codice Cliente Gestionale *"
                    value={newClient.client_code}
                    onChange={e => setNewClient(prev => ({ ...prev, client_code: e.target.value }))}
                    required
                  />
                </div>
                <div className="col-md-3">
                  <input
                    type="tel"
                    className="form-control"
                    placeholder="Telefono"
                    value={newClient.phone}
                    onChange={e => setNewClient(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
                <div className="col-md-3">
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Email"
                    value={newClient.email}
                    onChange={e => setNewClient(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
                <div className="col-md-2">
                  <button type="submit" className="btn btn-success w-100">Aggiungi</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

    {/* BOX LISTA CLIENTI - ARANCIONE */}
    <div className="card details-card clients-card">
    <div className="card-header">
        <h5 className="mb-0">👥 Clienti Coinvolti ({clients.length})</h5>
    </div>
    <div className="card-body">
        {clients.length === 0 ? (
        <p className="text-muted">Nessun cliente aggiunto</p>
        ) : (
        <div className="table-responsive">
            <table className="table table-sm table-hover">
            <thead>
                <tr>
                <th>Codice Cliente</th>
                <th>Telefono</th>
                <th>Email</th>
                <th>Email Inviata</th>
                <th>Prodotto Inviato</th>
                <th>Primo Feedback</th>
                <th>Prodotto Rientrato</th>
                <th>Ekinext Chiuso</th>
                </tr>
            </thead>
            <tbody>
                {clients.map(client => (
                <tr key={client.id}>
                    <td><strong>{client.client_code}</strong></td>
                    <td>{client.phone || '-'}</td>
                    <td>{client.email || '-'}</td>
                    <td>
                    <input
                        type="checkbox"
                        className="form-check-input"
                        checked={!!client.email_sent}
                        onChange={() => handleClientCheckbox(client.id, 'email_sent', client.email_sent)}
                        disabled={!isAdmin}
                    />
                    </td>
                    <td>
                    <input
                        type="checkbox"
                        className="form-check-input"
                        checked={!!client.product_sent}
                        onChange={() => handleClientCheckbox(client.id, 'product_sent', client.product_sent)}
                        disabled={!isAdmin}
                    />
                    </td>
                    <td>
                    <input
                        type="checkbox"
                        className="form-check-input"
                        checked={!!client.first_feedback_received}
                        onChange={() => handleClientCheckbox(client.id, 'first_feedback_received', client.first_feedback_received)}
                        disabled={!isAdmin}
                    />
                    </td>
                    <td>
                    <input
                        type="checkbox"
                        className="form-check-input"
                        checked={!!client.product_returned}
                        onChange={() => handleClientCheckbox(client.id, 'product_returned', client.product_returned)}
                        disabled={!isAdmin}
                    />
                    </td>
                    <td>
                    <input
                        type="checkbox"
                        className="form-check-input"
                        checked={!!client.ekinext_closed}
                        onChange={() => handleClientCheckbox(client.id, 'ekinext_closed', client.ekinext_closed)}
                        disabled={!isAdmin}
                    />
                    </td>
                </tr>
                ))}
            </tbody>
            </table>
        </div>
        )}
    </div>
    </div>


      {/* BOX FEEDBACK - VIOLA */}
      <div className="card details-card feedback-card">
        <div className="card-header">
          <h5 className="mb-0">💬 Feedback ({feedbacks.length})</h5>
        </div>
        <div className="card-body">
          {isAdmin && clients.length > 0 && (
            <form onSubmit={handleAddFeedback} className="mb-4 p-3 bg-light rounded">
              <div className="row">
                <div className="col-md-4 mb-2">
                  <select
                    className="form-select"
                    value={newFeedback.client_id}
                    onChange={e => setNewFeedback(prev => ({ ...prev, client_id: e.target.value }))}
                    required
                  >
                    <option value="">Seleziona Cliente</option>
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>{c.client_code}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-8 mb-2">
                  <ReactQuill
                    theme="snow"
                    value={newFeedback.feedback_text}
                    onChange={value => setNewFeedback(prev => ({ ...prev, feedback_text: value }))}
                    modules={quillModules}
                    placeholder="Inserisci feedback..."
                  />
                </div>
              </div>
              <button type="submit" className="btn btn-primary mt-2">Aggiungi Feedback</button>
            </form>
          )}

          {feedbacks.length === 0 ? (
            <p className="text-muted">Nessun feedback raccolto</p>
          ) : (
            <div className="list-group">
              {feedbacks.map(fb => (
                <div key={fb.id} className="list-group-item">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <strong className="text-primary">{fb.client_code}</strong>
                      <small className="text-muted ms-2">
                        {new Date(fb.created_at).toLocaleDateString('it-IT')}
                      </small>
                    </div>
                    {isAdmin && (
                      <div className="btn-group btn-group-sm">
                        {editingFeedback[fb.id] !== undefined && (
                          <button
                            className="btn btn-success"
                            onClick={() => handleUpdateFeedback(fb.id)}
                          >
                            Salva
                          </button>
                        )}
                        <button
                          className="btn btn-danger"
                          onClick={() => handleDeleteFeedback(fb.id)}
                        >
                          Elimina
                        </button>
                      </div>
                    )}
                  </div>
                  {isAdmin && editingFeedback[fb.id] !== undefined ? (
                    <ReactQuill
                      theme="snow"
                      value={editingFeedback[fb.id]}
                      onChange={value => setEditingFeedback(prev => ({ ...prev, [fb.id]: value }))}
                      modules={quillModules}
                    />
                  ) : (
                    <div
                      dangerouslySetInnerHTML={{ __html: fb.feedback_text }}
                      onClick={() => isAdmin && setEditingFeedback(prev => ({ ...prev, [fb.id]: fb.feedback_text }))}
                      style={{ cursor: isAdmin ? 'pointer' : 'default' }}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EkinextDetails;
